import React from 'react';
import { 
  formatDate, 
  formatTime,
  getAppointmentsForSlot, 
  getAvailabilityForSlot, 
  isSlotBookableMultipleServers,
  isToday as checkIsToday,
  calculateFloatingStartTime
} from '../../../utils/calendarHelpers';
import FloatingOverlay from './FloatingOverlay';

/**
 * Get the current date/time in a specific timezone
 */
const getCurrentTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    // Get the time string in the target timezone
    const timeStr = now.toLocaleString('en-US', { 
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    // Parse the localized string to extract hours and minutes
    const match = timeStr.match(/(\d+):(\d+)/);
    if (match) {
      return {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
        dateStr: now.toLocaleDateString('en-CA', { timeZone: timezone }) // YYYY-MM-DD format
      };
    }
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  } catch (e) {
    const now = new Date();
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  }
};

/**
 * WeekView - Calendar week grid view with time slots
 * 
 * SLOT AVAILABILITY FIX (Jan 2026):
 * Now receives slotOccupancy data which contains ALL bookings on the calendar
 * (not just the current customer's). This allows accurate availability calculation
 * for customers without exposing other customers' personal details.
 */
const WeekView = ({
  weekDays,
  timeSlots,
  appointments,
  slotOccupancy = { booked_slots: [], held_slots: [] },  // All bookings for availability
  availability,
  calendar,
  services,
  servers,
  user,
  isAdmin,
  isCustomer,
  currentTime,
  dragState,
  setDragState,
  setSelectedSlot,
  setSelectedAvailability,
  setSelectedAppointment,
  setContextMenu,
  setShowAppointmentModal,
  setShowEditAppointmentModal,
  setShowMultipleBookingsModal,
  setHoveredSlot,
  setTooltipPosition,
  setCurrentDate,
  setCurrentWeekStart,
  setViewMode,
  getWeekStart,
  token,
  onOpenMessageConsole
}) => {
  
  const handleDateClick = (date) => {
    setCurrentDate(date);
    setCurrentWeekStart(getWeekStart(date));
    setViewMode('day');
  };

  const handleCellRightClick = (e, day, hour) => {
    e.preventDefault();
    
    const slotAppointments = getAppointmentsForSlot(appointments, day, hour);
    const slotAvailability = getAvailabilityForSlot(availability, day, hour);
    
    if (slotAppointments.length > 0) {
      return;
    }
    
    const slotDateTime = new Date(day);
    slotDateTime.setHours(hour, 0, 0, 0);
    const isPastSlot = slotDateTime < new Date();
    
    // SLOT AVAILABILITY FIX (Jan 2026):
    // Pass slotOccupancy data for accurate availability (includes all bookings on calendar)
    const bookabilityCheck = isSlotBookableMultipleServers(
      calendar, 
      availability, 
      appointments, 
      day, 
      hour, 
      slotAvailability,
      slotOccupancy?.booked_slots || null,
      slotOccupancy?.held_slots || null,
      isCustomer
    );
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      hasAvailability: !!slotAvailability,
      isBookable: bookabilityCheck.bookable,
      isPastSlot: isPastSlot
    });
    setSelectedSlot({
      date: formatDate(day),
      hour: hour,
      time: `${hour.toString().padStart(2, '0')}:00`
    });
    
    if (slotAvailability) {
      setSelectedAvailability(slotAvailability);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[1200px] relative">
            {/* Day Headers - STYLING UPDATE (Jan 15, 2026):
                - Current date: Blue font + light blue background (overrides weekend)
                - Sat/Sun: Red font + light red background
                - Current date on weekend: Blue styling takes priority
                - Uses calendar timezone to determine "today"
            */}
            <div className="grid grid-cols-8 border-b bg-gray-50">
              <div className="p-4 border-r"></div>
              {(() => {
                // Get current date in calendar timezone for accurate "today" detection
                const calendarTimezone = calendar?.timezone || 'UTC';
                const tzTime = getCurrentTimeInTimezone(calendarTimezone);
                const todayDateStr = tzTime.dateStr; // YYYY-MM-DD format
                
                return weekDays.map((day, idx) => {
                  const dayDateStr = formatDate(day);
                  const isToday = dayDateStr === todayDateStr;
                  const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  // Determine header styling - current date overrides weekend
                  let headerBgClass = '';
                  let headerTextClass = 'text-gray-800';
                  let dateTextClass = 'text-gray-900';
                  
                  if (isToday) {
                    // Current date: Blue styling (highest priority)
                    headerBgClass = 'bg-blue-50';
                    headerTextClass = 'text-blue-600';
                    dateTextClass = 'text-blue-700';
                  } else if (isWeekend) {
                    // Weekend: Red styling
                    headerBgClass = 'bg-red-50';
                    headerTextClass = 'text-red-600';
                    dateTextClass = 'text-red-700';
                  }
                  
                  return (
                    <div 
                      key={idx} 
                      className={`p-4 text-center border-r last:border-r-0 cursor-pointer hover:bg-gray-100 ${headerBgClass}`}
                      onClick={() => handleDateClick(day)}
                    >
                    <div className={`font-semibold ${headerTextClass}`}>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-2xl font-bold mt-1 ${dateTextClass}`}>
                      {day.getDate()}
                    </div>
                  </div>
                  );
                });
              })()}
            </div>

            {/* Floating Calendar Availability Overlay */}
            {calendar?.booking_type === 'floating' && (
              <FloatingOverlay
                weekDays={weekDays}
                availability={availability}
                isAdmin={isAdmin}
                dragState={dragState}
                setDragState={setDragState}
                setSelectedSlot={setSelectedSlot}
                setShowAppointmentModal={setShowAppointmentModal}
                setContextMenu={setContextMenu}
                setSelectedAvailability={setSelectedAvailability}
                token={token}
              />
            )}

            {/* CURRENT TIME LINE INDICATOR (Jan 15, 2026):
                Blue horizontal line spanning the current date column to show current time
                - Shows at exact position if current time is within business hours
                - Shows at top if before business hours
                - Shows at bottom if after business hours
            */}
            {(() => {
              const calendarTimezone = calendar?.timezone || 'UTC';
              const tzTime = getCurrentTimeInTimezone(calendarTimezone);
              const todayIndex = weekDays.findIndex(day => formatDate(day) === tzTime.dateStr);
              
              if (todayIndex === -1) return null;
              
              // Calculate vertical position: each hour slot is ~80px (min-h-[80px])
              // First slot starts at hour from timeSlots[0]
              const startHour = timeSlots[0] || 8;
              const endHour = timeSlots[timeSlots.length - 1] || 20;
              const totalHours = endHour - startHour + 1;
              
              let topPercentage;
              let showTimeLabel = false;
              
              if (tzTime.hours < startHour) {
                // Before business hours - show at top with label
                topPercentage = 0;
                showTimeLabel = true;
              } else if (tzTime.hours > endHour) {
                // After business hours - show at bottom with label
                topPercentage = 100;
                showTimeLabel = true;
              } else {
                // Within business hours - show at exact position
                const hoursSinceStart = tzTime.hours - startHour + (tzTime.minutes / 60);
                topPercentage = (hoursSinceStart / totalHours) * 100;
              }
              
              // Column position: first col is time labels (12.5% = 1/8), then 7 day columns
              const leftPercentage = 12.5 + (todayIndex * (87.5 / 7));
              const widthPercentage = 87.5 / 7;
              
              // Format current time for label
              const timeLabel = `${tzTime.hours.toString().padStart(2, '0')}:${tzTime.minutes.toString().padStart(2, '0')}`;
              
              return (
                <div 
                  className="absolute z-30 pointer-events-none"
                  style={{ 
                    top: `${topPercentage}%`,
                    left: `${leftPercentage}%`,
                    width: `${widthPercentage}%`,
                    transform: topPercentage === 100 ? 'translateY(-100%)' : 'translateY(-50%)'
                  }}
                >
                  <div className="relative flex items-center">
                    {/* Blue dot at the start of the line */}
                    <div className="w-3 h-3 bg-blue-500 rounded-full shadow-md flex-shrink-0" />
                    {/* Blue line spanning the column */}
                    <div className="h-0.5 bg-blue-500 flex-grow shadow-sm" />
                    {/* Time label when outside business hours */}
                    {showTimeLabel && (
                      <div className="absolute -top-5 left-0 text-xs font-medium text-blue-600 bg-blue-50 px-1 rounded">
                        {timeLabel}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Time Slots */}
            {timeSlots.map((hour) => {
              return (
                <div key={hour} className="grid grid-cols-8 border-b last:border-b-0">
                  {/* Time Label */}
                  <div className="p-4 border-r bg-gray-50 text-sm font-medium text-gray-600">
                    {formatTime(hour)}
                  </div>
                
                  {/* Day Cells */}
                  {weekDays.map((day, idx) => {
                    const slotAppointments = getAppointmentsForSlot(appointments, day, hour);
                    const slotAvailability = getAvailabilityForSlot(availability, day, hour);
                    const slotKey = `${formatDate(day)}-${hour}`;
                    
                    // Use calendar timezone for current time indicator (Option A)
                    const calendarTimezone = calendar?.timezone || 'UTC';
                    const tzTime = getCurrentTimeInTimezone(calendarTimezone);
                    const dayDateStr = formatDate(day);
                    const isTodayInCalendarTz = dayDateStr === tzTime.dateStr;
                    const isCurrentHour = hour === tzTime.hours && isTodayInCalendarTz;
                    const timeIndicatorPosition = isCurrentHour ? (tzTime.minutes / 60) * 100 : null;

                    // Check if slot is in the past using calendar timezone
                    const slotDateTime = new Date(day);
                    slotDateTime.setHours(hour, 0, 0, 0);
                    // For past slot detection, we compare in calendar timezone
                    const isPastSlot = (dayDateStr < tzTime.dateStr) || 
                      (dayDateStr === tzTime.dateStr && hour < tzTime.hours);

                    const hasMyAppointment = user?.role === 'customer' && 
                      slotAppointments.some(apt => apt.customer_id === user.id);
                    
                    // SLOT AVAILABILITY FIX (Jan 2026):
                    // Use slotOccupancy for accurate availability check (includes all bookings)
                    const bookabilityCheck = isSlotBookableMultipleServers(
                      calendar, 
                      availability, 
                      appointments, 
                      day, 
                      hour, 
                      slotAvailability,
                      slotOccupancy?.booked_slots || null,
                      slotOccupancy?.held_slots || null,
                      isCustomer && isCustomer(),
                      services
                    );
                    const isBookable = bookabilityCheck.bookable && !isPastSlot;
                    
                    // Check if slot is fully booked (applies to both admins and customers)
                    // This is used for:
                    // 1. Hiding tooltip on fully booked slots
                    // 2. Showing grey background instead of green
                    const isFullyBooked = bookabilityCheck.reason === 'slot_booked' || 
                                          bookabilityCheck.reason === 'all_servers_busy';
                    
                    // SLOT AVAILABILITY FIX (Jan 2026):
                    // For customers, check if slot is booked by ANYONE (not just them)
                    const isSlotBookedByOthers = isCustomer && isCustomer() && slotOccupancy?.booked_slots?.some(slot => {
                      const slotDate = slot.date;
                      const slotTime = slot.time;
                      return slotDate === dayDateStr && slotTime === `${hour.toString().padStart(2, '0')}:00`;
                    });
                    
                    // Determine background color
                    let bgColorClass = '';
                    
                    // Check if slot has bookings (for background color distinction)
                    const hasBookings = slotAppointments.length > 0;
                    
                    if (isPastSlot && slotAvailability) {
                      bgColorClass = 'bg-gray-400';
                    } else if (user?.role === 'customer') {
                      if (hasMyAppointment) {
                        bgColorClass = 'bg-blue-100';
                      } else if (isSlotBookedByOthers) {
                        // Slot is booked by another customer - show as unavailable (not red, just gray)
                        bgColorClass = 'bg-gray-200';
                      } else if (slotAppointments.length > 0) {
                        bgColorClass = 'bg-red-100';
                      } else if (slotAvailability) {
                        if (slotAvailability.is_on_hold) {
                          bgColorClass = '';
                        } else {
                          bgColorClass = isBookable ? 'bg-green-100' : 'bg-orange-100';
                        }
                      }
                    } else {
                      // Admin view - Color Legend:
                      // Green = Available/Bookable
                      // Blue = Has booking(s), not bookable
                      // Orange = On Hold (hidden from customers)
                      // Gray = Past or fully booked (all servers busy)
                      if (slotAvailability) {
                        if (isPastSlot) {
                          bgColorClass = 'bg-gray-400';
                        } else if (hasBookings) {
                          // Slot has bookings - show as booked (blue tint - distinct from orange)
                          bgColorClass = 'bg-blue-50';
                        } else if (isFullyBooked) {
                          // Fully booked slot (all servers busy) - lighter grey
                          bgColorClass = 'bg-gray-100';
                        } else if (slotAvailability.is_on_hold) {
                          // On Hold - orange tint
                          bgColorClass = 'bg-orange-50';
                        } else {
                          // Available/bookable slot - green
                          bgColorClass = 'bg-green-50';
                        }
                      }
                    }
                    
                    return (
                      <div
                        key={idx}
                        className={`relative p-2 border-r last:border-r-0 min-h-[80px] transition-colors ${bgColorClass} ${
                          (isAdmin() || (user?.role === 'customer' && slotAvailability && slotAppointments.length === 0)) && isBookable && !isPastSlot
                            ? 'cursor-pointer hover:opacity-90' : ''
                        }`}
                        onContextMenu={(e) => {
                          if (isAdmin()) {
                            handleCellRightClick(e, day, hour);
                          } else if (user?.role === 'customer' && hasMyAppointment) {
                            e.preventDefault();
                            const myAppointment = slotAppointments.find(apt => apt.customer_id === user.id);
                            if (myAppointment) {
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                isCustomerBooking: true
                              });
                              setSelectedAppointment(myAppointment);
                            }
                          }
                        }}
                        onClick={(e) => {
                          if (e.target === e.currentTarget || e.target.classList.contains('time-slot-bg')) {
                            if ((isAdmin() || user?.role === 'customer') && slotAvailability && isBookable && !isPastSlot) {
                              let startTime = `${hour.toString().padStart(2, '0')}:00`;
                              
                              if (calendar?.booking_type === 'floating') {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const relativeY = (e.clientY - rect.top) / rect.height;
                                startTime = calculateFloatingStartTime(relativeY, hour);
                              }
                              
                              setSelectedSlot({
                                date: formatDate(day),
                                hour: hour,
                                time: startTime
                              });
                              setShowAppointmentModal(true);
                            }
                          }
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          
                          // Simplified tooltip logic:
                          // 1) Slot is bookable → show services available
                          // 2) Slot is not bookable:
                          //    2.1) No bookings → nothing to show
                          //    2.2) Has bookings → show booking details
                          
                          const slotIsBookable = slotAvailability && isBookable && !isPastSlot && !isFullyBooked;
                          const hasBookings = slotAppointments.length > 0;
                          
                          if (slotIsBookable) {
                            // Case 1: Show available services
                            let availableServers = [];
                            if (calendar?.multiple_servers && isAdmin()) {
                              const dateStr = formatDate(day);
                              const calendarServers = calendar.server_ids || [];
                              
                              const workingServers = availability.filter(avail => {
                                if (avail.date !== dateStr || !avail.server_id || !calendarServers.includes(avail.server_id)) {
                                  return false;
                                }
                                const serverStartHour = parseInt(avail.start_time.split(':')[0]);
                                const serverEndHour = parseInt(avail.end_time.split(':')[0]);
                                return hour >= serverStartHour && hour < serverEndHour;
                              });
                              
                              const bookedServerIds = slotAppointments
                                .filter(apt => apt.server_id)
                                .map(apt => apt.server_id);
                              
                              availableServers = [...new Set(
                                workingServers
                                  .filter(ws => !bookedServerIds.includes(ws.server_id))
                                  .map(ws => ws.server_id)
                              )];
                            }
                            
                            setHoveredSlot(slotKey);
                            setTooltipPosition({
                              x: rect.left,
                              y: rect.top,
                              width: rect.width,
                              appointments: [],
                              availableServerIds: availableServers,
                              slotDate: formatDate(day),
                              slotHour: hour,
                              isOnHold: slotAvailability?.is_on_hold || false
                            });
                          } else if (hasBookings) {
                            // Case 2.2: Show booking details
                            setHoveredSlot(slotKey);
                            setTooltipPosition({
                              x: rect.left,
                              y: rect.top,
                              width: rect.width,
                              appointments: slotAppointments,
                              availableServerIds: [],
                              slotDate: formatDate(day),
                              slotHour: hour,
                              isOnHold: false
                            });
                          }
                          // Case 2.1: Not bookable + no bookings → no tooltip (do nothing)
                        }}
                        onMouseLeave={() => {
                          setHoveredSlot(null);
                          setTooltipPosition(null);
                        }}
                      >
                        {/* Clickable background layer */}
                        {slotAvailability && isBookable && !isPastSlot && (
                          <div 
                            className="absolute inset-0 time-slot-bg"
                            style={{ zIndex: 1 }}
                          />
                        )}

                        {/* Appointments Display */}
                        {calendar?.multiple_servers && slotAppointments.length > 0 ? (
                          // Multiple Servers Mode: Badge
                          <div
                            className={`relative w-full pt-2 px-2 transition-opacity ${isPastSlot ? 'opacity-50' : ''}`}
                            style={{ zIndex: 10, pointerEvents: 'none' }}
                          >
                            <div 
                              className={`inline-block px-2 py-1.5 rounded-md shadow-sm transition-shadow ${
                                isPastSlot 
                                  ? 'bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 cursor-default'
                                  : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300 hover:shadow-md cursor-pointer hover:opacity-80'
                              }`}
                              style={{ pointerEvents: 'auto' }}
                              title={slotAppointments.map(apt => 
                                `${apt.customer_name} - ${apt.service_name}${apt.server_id && servers.length > 0 ? ' (' + (servers.find(s => s.id === apt.server_id)?.server_name || 'Server') + ')' : ''}`
                              ).join('\n')}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                // Show booking details tooltip for booked slots
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHoveredSlot(slotKey);
                                setTooltipPosition({
                                  x: rect.left,
                                  y: rect.top,
                                  width: rect.width,
                                  appointments: slotAppointments,
                                  availableServerIds: [],
                                  slotDate: formatDate(day),
                                  slotHour: hour,
                                  isOnHold: false
                                });
                              }}
                              onMouseLeave={() => {
                                setHoveredSlot(null);
                                setTooltipPosition(null);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isAdmin() && !isPastSlot) {
                                  setSelectedSlot({
                                    date: formatDate(day),
                                    hour: hour,
                                    time: `${hour.toString().padStart(2, '0')}:00`,
                                    appointments: slotAppointments
                                  });
                                  setShowMultipleBookingsModal(true);
                                }
                              }}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <svg className={`w-3.5 h-3.5 ${isPastSlot ? 'text-gray-500' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                </svg>
                                <span className={`text-sm font-bold ${isPastSlot ? 'text-gray-600' : 'text-blue-700'}`}>
                                  {slotAppointments.length}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Single Server Mode: Card UI
                          // Group appointments by service_id + start_time to handle multi-customer bookings
                          (() => {
                            // Create groups of related appointments
                            const processedGroups = new Set();
                            
                            return slotAppointments.map((apt, aptIdx) => {
                              // Create a unique key for this appointment group
                              const groupKey = `${apt.service_id}-${apt.start_time}`;
                              
                              // Skip if we've already rendered this group
                              if (processedGroups.has(groupKey)) {
                                return null;
                              }
                              processedGroups.add(groupKey);
                              
                              const service = services.find(s => s.id === apt.service_id);
                              const serviceColor = service?.color_code || '#3B82F6';
                              const bgColor = isPastSlot ? '#E5E7EB40' : serviceColor + '20';
                              const displayColor = isPastSlot ? '#6B7280' : serviceColor;
                              const isMyAppointment = user?.role === 'customer' && apt.customer_id === user.id;
                              
                              // Find all related appointments at the same time slot with same service (multi-customer bookings)
                              const relatedAppointments = slotAppointments.filter(
                                relApt => relApt.service_id === apt.service_id && 
                                          relApt.start_time === apt.start_time
                              );
                              const hasMultipleCustomers = relatedAppointments.length > 1;
                              
                              // For multi-customer bookings, check if ANY of them is the current user's appointment
                              const isAnyMyAppointment = user?.role === 'customer' && 
                                relatedAppointments.some(relApt => relApt.customer_id === user.id);
                              
                              let tooltipText = '';
                              if (isAdmin()) {
                                const aptTime = apt.start_time?.split('T')[1]?.substring(0, 5);
                                tooltipText = `Time: ${aptTime}\nService: ${apt.service_name}`;
                                
                                // Show all customers for multi-customer bookings
                                if (hasMultipleCustomers) {
                                  tooltipText += `\nCustomers (${relatedAppointments.length}):`;
                                  relatedAppointments.forEach((relApt, idx) => {
                                    tooltipText += `\n  ${idx + 1}. ${relApt.customer_name}`;
                                  });
                                } else {
                                  tooltipText += `\nCustomer: ${apt.customer_name}`;
                                }
                              
                              if (apt.server_id && servers.length > 0) {
                                const server = servers.find(s => s.id === apt.server_id);
                                tooltipText += `\nServer: ${server ? server.server_name : 'Unknown'}`;
                              } else if (calendar?.multiple_servers) {
                                tooltipText += `\nServer: Not Assigned`;
                              }
                              
                              if (apt.notes) {
                                tooltipText += `\nNotes: ${apt.notes}`;
                              }
                              
                              if (isPastSlot) {
                                tooltipText += `\n[Past Booking - Display Only]`;
                              }
                            } else if (isMyAppointment) {
                              // CUSTOMER BOOKING TOOLTIP (Jan 15, 2026):
                              // Show booking details to customers for their own appointments
                              const aptTime = apt.start_time?.split('T')[1]?.substring(0, 5);
                              const aptDate = apt.start_time?.split('T')[0];
                              tooltipText = `Your Booking\n`;
                              tooltipText += `Date: ${aptDate}\n`;
                              tooltipText += `Time: ${aptTime}\n`;
                              tooltipText += `Service: ${apt.service_name}`;
                              
                              if (apt.server_id && servers.length > 0) {
                                const server = servers.find(s => s.id === apt.server_id);
                                if (server) {
                                  tooltipText += `\nWith: ${server.server_name}`;
                                }
                              }
                              
                              if (apt.notes) {
                                tooltipText += `\nNotes: ${apt.notes}`;
                              }
                              
                              if (isPastSlot) {
                                tooltipText += `\n[Past Appointment]`;
                              } else {
                                tooltipText += `\n\nRight-click to cancel`;
                              }
                            }
                            
                            return (
                              <div
                                key={aptIdx}
                                className={`relative border-l-4 p-2 mb-2 rounded text-xs transition-opacity ${
                                  isPastSlot ? 'opacity-60 cursor-default' : (isAdmin() ? 'cursor-pointer hover:opacity-80' : '')
                                }`}
                                style={{
                                  backgroundColor: bgColor,
                                  borderLeftColor: displayColor,
                                  zIndex: 10
                                }}
                                onMouseEnter={(e) => {
                                  e.stopPropagation();
                                  // Show booking details tooltip for booked slots
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setHoveredSlot(slotKey);
                                  setTooltipPosition({
                                    x: rect.left,
                                    y: rect.top,
                                    width: rect.width,
                                    appointments: slotAppointments,
                                    availableServerIds: [],
                                    slotDate: formatDate(day),
                                    slotHour: hour,
                                    isOnHold: false
                                  });
                                }}
                                onMouseLeave={() => {
                                  setHoveredSlot(null);
                                  setTooltipPosition(null);
                                }}
                                onContextMenu={(e) => {
                                  if (isAdmin() && !isPastSlot) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Set context menu for booking with message option
                                    setContextMenu({
                                      x: e.clientX,
                                      y: e.clientY,
                                      isBookingContextMenu: true,
                                      appointment: apt,
                                      relatedAppointments: relatedAppointments
                                    });
                                    setSelectedAppointment({
                                      ...apt,
                                      relatedAppointments: relatedAppointments
                                    });
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isAdmin() && !isPastSlot) {
                                    // Pass the clicked appointment with related appointments
                                    setSelectedAppointment({
                                      ...apt,
                                      relatedAppointments: relatedAppointments
                                    });
                                    setShowEditAppointmentModal(true);
                                  }
                                }}
                              >
                                <div className="font-semibold" style={{ color: displayColor }}>
                                  {isAdmin() || isAnyMyAppointment ? (
                                    hasMultipleCustomers ? (
                                      // Show all customer names for multi-customer bookings
                                      <span>
                                        {relatedAppointments.map(ra => ra.customer_name).join(', ')}
                                        <span className="text-xs font-normal ml-1">({relatedAppointments.length})</span>
                                      </span>
                                    ) : (
                                      apt.customer_name
                                    )
                                  ) : 'Booked'}
                                </div>
                                <div style={{ color: displayColor, opacity: 0.8 }}>
                                  {apt.service_name}
                                </div>
                                {isAdmin() && apt.server_id && servers.length > 0 && (
                                  <div style={{ color: displayColor, opacity: 0.7, fontSize: '0.65rem', marginTop: '2px' }}>
                                    {servers.find(s => s.id === apt.server_id)?.server_name || 'Unknown Server'}
                                  </div>
                                )}
                                {isPastSlot && (
                                  <div className="text-xs text-gray-500 mt-1 italic">Past</div>
                                )}
                              </div>
                            );
                          })})()
                        )}
                        
                        {/* Status text for Fixed calendars (Jan 15, 2026):
                            - For customers: Show "Booked" when slot is taken by others
                            - This prevents confusion where booked slots showed "Available" with gray background
                        */}
                        {slotAvailability && slotAppointments.length === 0 && calendar?.booking_type !== 'floating' && (
                          <div className={`text-xs italic ${
                            isPastSlot 
                              ? 'text-gray-500' 
                              : isSlotBookedByOthers
                                ? 'text-gray-600'
                              : slotAvailability.is_on_hold 
                                ? 'text-orange-600' 
                                : 'text-green-600'
                          }`}>
                            {isPastSlot 
                              ? 'Past' 
                              : isSlotBookedByOthers
                                ? 'Booked'
                              : slotAvailability.is_on_hold 
                                ? 'On Hold' 
                                : 'Available'
                            }
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Instructions - Legend (Jan 15, 2026):
          Updated to show gray for "Booked" slots to match the actual display
      */}
      <div className="mt-4 text-sm text-gray-600 text-center">
        {user?.role === 'customer' ? (
          <div className="space-y-1">
            <div className="flex items-center justify-center space-x-6">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-100 border border-green-300 mr-2"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-200 border border-gray-300 mr-2"></div>
                <span>Booked</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-100 border border-blue-300 mr-2"></div>
                <span>Your Bookings</span>
              </div>
            </div>
            <div className="mt-2">
              Click on available slots to book • Right-click on your bookings to cancel
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center justify-center space-x-6 flex-wrap gap-2">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-50 border border-green-300 mr-2"></div>
                <span>Available</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 border border-gray-300 mr-2"></div>
                <span>Fully Booked</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-300 border border-gray-400 mr-2"></div>
                <span>Past</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-orange-50 border border-orange-300 mr-2"></div>
                <span>On Hold</span>
              </div>
            </div>
            <div className="mt-2">
              Right-click on any time slot to create an appointment or set availability
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WeekView;
